import React, { useCallback, useEffect, useState } from 'react';

import scss from './ConciergeGeneric.module.scss';
import { CONCIERGE_IMAGE_URL, DEFAULT_CONCIERGE_NAME, EVENT_OPEN_MESSAGING_DRAWER } from '@helper/constants';
import MeetConciergeModal from './MeetConciergeModal';
import { Button } from 'react-bootstrap';
import MessagingConciergePreferencesModal from '@components/Messaging/MessagingConciergePreferencesModal';
import Loading from "@ui/Loading";;
import { getConciergeInfo, IAssignedConcierge } from '@api/getConciergeInfo';
import MessagingLastMessage from '@components/MessagingLastMessage';
import { getMessagingKey } from '@helper/getConfigValues';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

import ClientOnly from '../../ClientOnly';
import { QuickLoginRegisterProps } from '@components/QuickLoginRegister/types';
import { gtagEvent } from '@helper/gtm';

const QuickLoginRegisterModal = React.lazy(() => import('@components/QuickLoginRegister'));

export interface IConciergeGenericProps {
    name: string;
    userId: string;
    isConciergeChannelCreated: boolean;
    isOnDashboard: boolean;
    compressed?: boolean;
    staticVersion?: boolean;
    onLoggedIn?: () => void;
}

const ConciergeGeneric: React.FC<IConciergeGenericProps> = ({ name, userId, isConciergeChannelCreated, isOnDashboard, compressed, staticVersion, onLoggedIn }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [conciergeInfo, setConciergeInfo] = useState<IAssignedConcierge | null>(null);
    const [showInfo, setShowInfo] = useState<boolean>(false);
    const [showPreferences, setShowPreferences] = useState<boolean>(false);
    const [isUnread, setIsUnread] = useState<boolean>(false);
    const [showQuickLoginModal, setShowQuickLoginModal] = useState<boolean>(false);

    useEffect(() => {
        if (!isConciergeChannelCreated) {
            setLoading(false);
            return;
        }
        
        const fetchData = async () => {
            const data: IAssignedConcierge = await getConciergeInfo();

            setLoading(false);

            if (!data) {
                return;
            }

            setConciergeInfo(data);
        };
        
        fetchData();
    }, [isConciergeChannelCreated]);

    const handleInfoClose = () => {
        setShowInfo(false);
    }

    const handleMessageClick = (preferencesSet: boolean) => {
        isOnDashboard && gtagEvent('concierge_dashboard_click');
        
        if (preferencesSet) {
            window.location.href = '/inbox/';
        }
        else if (!isOnDashboard && !userId) {
            setShowQuickLoginModal(true);
        }
        else {
            setShowPreferences(true);
        }
    }

    const hideLoginModal = () => {
        setShowQuickLoginModal(false);
    }

    const handlePreferencesClose = () => {
        setShowPreferences(false);
    }

    const openPrefModal = () => {
        setShowInfo(false);
        setTimeout(() => {
            setShowPreferences(true);
        }, 500);
    }

    const handleMessageClickWithPreferences = useCallback(() => {
        handleMessageClick(!!conciergeInfo?.IsUserPreferenceSelected);
    }, [handleMessageClick, conciergeInfo?.IsUserPreferenceSelected]);

    const handleConciergeChatClick = () => {
        if (!!window) {
            const event = new CustomEvent(EVENT_OPEN_MESSAGING_DRAWER);
            window.dispatchEvent(event);
        }
    };

    const conciergeImage = !!conciergeInfo?.StaffProfileImage ? conciergeInfo.StaffProfileImage : CONCIERGE_IMAGE_URL;
    const conciergeName = !!conciergeInfo?.StaffFirstName ? conciergeInfo.StaffFirstName : DEFAULT_CONCIERGE_NAME;

    if (loading) {
        return (
            <div className={'d-flex justify-content-center w-100'}>
                <Loading />
            </div>
        );
    }
    
    return (
        <div className={`${scss.cardContainer} ${isOnDashboard ? scss.lightContainer : scss.lightContainer} ${!!compressed ? scss.backWhite : ''}`}>
            <div className={scss.headingContainer}>
                <div className={scss.imageContainer}>
                    {!conciergeInfo?.StaffId ? (<>
                        <div className={`${scss.statusIcon} ${scss.infoIcon}`} onClick={() => setShowInfo(true)}>
                            <i className='fal fa-info-circle align-top'></i>
                        </div>
                    </>)
                    : isUnread && (
                        <div className={`${scss.statusIcon} ${scss.unreadIndicator}`}>
                            <i className='far fa-bell fa-shake'></i>
                        </div>
                    )}
                    <img
                        className={scss.imageDarcy}
                        src={conciergeImage}
                        alt={conciergeName}
                    />
                </div>
                <div className='position-relative flex-grow-1'>
                    {!!compressed ?
                        <div className={scss.cardQuestion}>Got questions about our packages?</div> :
                        <div className={scss.weddingConciergeHeading}>Wedding Planner</div>}
                    <div className={scss.mainText}>
                        Ask {conciergeName}
                    </div>
                    <div className={scss.subText}>
                        {(!!conciergeInfo?.StaffId && !!conciergeInfo?.IsOnline) ?
                            <>
                                <div className={`${scss.greenDot} ${scss.online}`}></div>
                                <span className={scss.smallText}>Online</span>
                            </> :
                            <>
                                <div className={scss.greenDot}></div>
                                <span className={scss.smallText}>
                                    {!!compressed || !isOnDashboard ? 'Responds within 1 day' : 'Typically responds within 1 business day'}
                                </span>
                            </>
                        }
                    </div>
                    {!compressed ? <div className={`${scss.inboxButton} ${isOnDashboard ? 'd-none d-sm-flex' : ''}`} onClick={handleMessageClickWithPreferences}>
                        <Icon icon={faChevronRight} />
                    </div> :
                    <div className={scss.compressedMessageButton} onClick={handleMessageClickWithPreferences}>
                        Message {conciergeName} &rarr;
                    </div>}
                    {!compressed && <>
                        {!!conciergeInfo?.IsUserPreferenceSelected ? <a href="/inbox/"className={`${scss.inboxButton} d-flex ${isOnDashboard ? 'd-sm-none' : ''}`}>
                            <Icon icon={faChevronRight} />
                        </a> : (isOnDashboard && <Link to='concierge-preferences/' className={`${scss.inboxButton} d-flex ${isOnDashboard ? 'd-sm-none' : ''}`}>
                            <Icon icon={faChevronRight} />
                        </Link>)}
                    </>}
                </div>
            </div>

            {!compressed && <div className={scss.mainContent} onClick={handleMessageClickWithPreferences}>
                {(!!conciergeInfo?.StaffId && !staticVersion) ? (<>
                    <div className={`card mt-2 ${scss.messagesContainer}`}>
                        <div className={scss.messagingChannelContainer}>
                            <MessagingLastMessage
                                appKey={getMessagingKey()}
                                userId={userId}
                                channelUrl={!!conciergeInfo.ChannelUrl ? conciergeInfo.ChannelUrl : ''}
                                regularUpdate={true}
                                setUnreadStatus={setIsUnread}
                            />
                        </div>
                    </div>
                    </>) : (!isOnDashboard ?
                        <div className={scss.messagePlainText}>
                            {!conciergeInfo?.StaffId 
                                ? 'Not sure if a package is right for you? Darcy and her team of experienced wedding planners are here to help. Ask her anything!'
                                : 'Not sure if a package is right for you? Our team of experienced wedding planners are here to help. Ask us anything!'
                            }
                        </div> 
                        :
                        <div className={`card mt-2 ${scss.messagesContainer}`}>
                            <div className={'card-body p-2'}>
                                <div className={`mt-2 ${scss.messageHeader}`}>
                                    <img className={scss.imageDarcySmall} src={CONCIERGE_IMAGE_URL} alt={conciergeName} />
                                    <strong>{conciergeName}</strong>
                                </div>
                                <div>
                                    <div className={`rounded-3 ${scss.messageBubble}`}>
                                        <div className={scss.messageText}>
                                            Hi {name}, let me know how I can help you with anything from finding a venue to building a supplier package... or anything else!
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
            </div>}
            {!!conciergeInfo?.StaffId ? (!compressed &&
                    (!staticVersion ? <>
                        <a href={'/inbox/'} className={`btn btn-outline-secondary mt-3`}>View All Messages</a>
                    </> :
                    <>
                        <Button onClick={(e) => { e.preventDefault(); handleConciergeChatClick(); }} className={'btn btn-primary mt-3'}>Message {conciergeName}</Button>
                    </>)
                ) : (<>
                {!compressed && (!!conciergeInfo?.IsUserPreferenceSelected ?
                    <Button onClick={handleMessageClickWithPreferences} className={'btn btn-primary mt-3'}>Message {conciergeName}</Button>
                    : <>
                        <Button onClick={handleMessageClickWithPreferences} className={`btn btn-primary mt-3 ${isOnDashboard ? 'd-none d-sm-block' : ''}`}>Message {conciergeName}</Button>
                        {isOnDashboard && <Link to='concierge-preferences/' className={`${scss.inboxButton} d-flex d-sm-none d-block mt-3`}>
                            <Button onClick={handleMessageClickWithPreferences} className={'btn btn-primary flex-grow-1'}>Message {conciergeName}</Button>
                        </Link>}
                    </>
                )}

                <MeetConciergeModal
                    show={showInfo}
                    isOnDashboard={isOnDashboard}
                    handleClose={handleInfoClose}
                    openPrefModal={openPrefModal}
                />
                {showQuickLoginModal && <ClientOnly<QuickLoginRegisterProps> Component={QuickLoginRegisterModal}
                    showModal={showQuickLoginModal}
                    onHideModal={hideLoginModal}
                />}
                {!conciergeInfo?.IsUserPreferenceSelected && showPreferences && (
                    <MessagingConciergePreferencesModal
                        handleClose={handlePreferencesClose}
                    />
                )}
            </>)}
        </div>
    )
}

export default ConciergeGeneric
